import React, { useState, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import {
  DollarSign, Users, Phone, Eye, Target, TrendingUp,
  RefreshCw, ChevronRight, ArrowRight, Zap, Info
} from 'lucide-react';

// ─── Editable number field ──────────────────────────────────────────────────
const EditableNum = ({ value, onChange, prefix, suffix, placeholder, big, highlight, muted }) => {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState('');
  const inputRef = useRef();

  const start = () => {
    setLocal(value === null || value === undefined ? '' : String(value));
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 10);
  };

  const commit = () => {
    setEditing(false);
    const raw = local.replace(/[^0-9.]/g, '');
    const n = parseFloat(raw);
    onChange(isNaN(n) ? null : n);
  };

  const display = value === null || value === undefined || value === ''
    ? (muted ? '—' : '')
    : (prefix || '') + Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 }) + (suffix || '');

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Tab') commit(); if (e.key === 'Escape') { setEditing(false); } }}
        className={`border-b-2 border-cyan-400 bg-transparent outline-none text-center w-full tabular-nums font-semibold ${big ? 'text-3xl' : 'text-xl'} text-slate-900`}
        type="number"
        min="0"
        step="any"
        placeholder={placeholder || '0'}
      />
    );
  }

  return (
    <div
      onClick={start}
      title="Click to edit"
      className={`cursor-pointer select-none text-center w-full tabular-nums font-semibold group relative
        ${big ? 'text-3xl' : 'text-xl'}
        ${muted ? 'text-slate-400' : highlight ? 'text-cyan-700' : 'text-slate-900'}
        hover:text-cyan-600`}
    >
      {display || <span className="text-slate-300 text-base">{placeholder || 'click'}</span>}
      <span className="absolute -top-0.5 -right-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="text-xs text-cyan-400">✎</span>
      </span>
    </div>
  );
};

// ─── Rate badge between stages ──────────────────────────────────────────────
const RateBadge = ({ rate, onChange, label }) => {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState('');
  const inputRef = useRef();

  const start = () => {
    setLocal(rate === null || rate === undefined ? '' : String(rate));
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 10);
  };

  const commit = () => {
    setEditing(false);
    const n = parseFloat(local);
    onChange(isNaN(n) ? null : Math.min(100, Math.max(0, n)));
  };

  const color = rate === null ? 'bg-slate-100 text-slate-500' :
    rate >= 50 ? 'bg-green-100 text-green-700 border-green-200' :
    rate >= 25 ? 'bg-amber-100 text-amber-700 border-amber-200' :
    'bg-red-100 text-red-600 border-red-200';

  return (
    <div className="flex flex-col items-center gap-1 px-2">
      <span className="text-xs text-slate-400">{label}</span>
      {editing ? (
        <div className="flex items-center gap-0.5">
          <input
            ref={inputRef}
            value={local}
            onChange={e => setLocal(e.target.value)}
            onBlur={commit}
            onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
            className="w-16 text-center border border-cyan-400 rounded-full px-2 py-0.5 text-xs outline-none bg-white text-slate-800"
            type="number" min="0" max="100" step="0.1"
          />
          <span className="text-xs text-slate-500">%</span>
        </div>
      ) : (
        <button
          onClick={start}
          className={`border rounded-full px-2.5 py-0.5 text-xs font-semibold cursor-pointer hover:ring-2 hover:ring-cyan-300 transition-all ${color}`}
          title="Click to edit rate"
        >
          {rate !== null && rate !== undefined ? `${Number(rate).toFixed(1)}%` : 'set %'}
        </button>
      )}
      <ArrowRight size={14} className="text-slate-300" />
    </div>
  );
};

// ─── Stage Card ──────────────────────────────────────────────────────────────
const StageCard = ({ icon: Icon, title, color, children, width, highlight }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.2 }}
    className={`flex-shrink-0 ${width || 'w-44'}`}
  >
    <Card className={`bg-white shadow-sm h-full ${highlight ? 'ring-2 ring-cyan-400' : ''}`}
      style={{ borderTop: `3px solid ${color}` }}>
      <CardContent className="p-4">
        <div className="flex items-center gap-1.5 mb-3">
          <Icon size={14} style={{ color }} />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</span>
        </div>
        {children}
      </CardContent>
    </Card>
  </motion.div>
);

// ─── Sub-metric row inside a card ────────────────────────────────────────────
const SubRow = ({ label, value, prefix, suffix, muted }) => (
  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
    <span className="text-xs text-slate-400">{label}</span>
    <span className={`text-xs font-semibold tabular-nums ${muted ? 'text-slate-400' : 'text-slate-700'}`}>
      {value === null || value === undefined ? '—' : (prefix || '') + Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 }) + (suffix || '')}
    </span>
  </div>
);

// ─── Main Calculator ─────────────────────────────────────────────────────────
const calc = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const pct = (a, b) => (calc(b) > 0 ? Math.round(calc(a) / calc(b) * 1000) / 10 : null);
const apply = (base, rate) => (calc(base) !== null && calc(rate) !== null ? Math.round(calc(base) * calc(rate) / 100) : null);
const fmt$ = v => v !== null && v !== undefined ? '$' + Math.round(v).toLocaleString() : '—';

const DEFAULTS = {
  spend: 38000,
  leads: null,
  cpl: null,
  booked: null,
  booking_rate: null,
  shows: null,
  show_rate: null,
  fa_presented: null,
  fa_rate: null,
  sold_same_day: null,
  same_day_close_rate: null,
  sold_previous: 0,
  prev_close_rate: null,
  avg_case_value: 32000,
  arches_per_case: 1.6,
};

export default function FlowCalculatorPage() {
  const [v, setV] = useState(DEFAULTS);
  const [scenario, setScenario] = useState('Custom');

  const set = useCallback((key, val) => setV(prev => ({ ...prev, [key]: val })), []);

  // ── Derived calculations (cascade) ──────────────────────────────────────
  // Leads
  const leads_calc = v.leads !== null ? calc(v.leads)
    : (calc(v.spend) !== null && calc(v.cpl) !== null && calc(v.cpl) > 0)
      ? Math.round(calc(v.spend) / calc(v.cpl))
      : null;
  const cpl_calc = v.cpl !== null ? calc(v.cpl)
    : (calc(v.spend) !== null && leads_calc !== null && leads_calc > 0)
      ? Math.round(calc(v.spend) / leads_calc * 100) / 100
      : null;

  // Bookings
  const booked_calc = v.booked !== null ? calc(v.booked)
    : apply(leads_calc, v.booking_rate);
  const booking_rate_calc = v.booking_rate !== null ? calc(v.booking_rate)
    : pct(booked_calc, leads_calc);

  // Shows
  const shows_calc = v.shows !== null ? calc(v.shows)
    : apply(booked_calc, v.show_rate);
  const show_rate_calc = v.show_rate !== null ? calc(v.show_rate)
    : pct(shows_calc, booked_calc);

  // FA Presented
  const fa_presented_calc = v.fa_presented !== null ? calc(v.fa_presented)
    : apply(shows_calc, v.fa_rate);
  const fa_rate_calc = v.fa_rate !== null ? calc(v.fa_rate)
    : pct(fa_presented_calc, shows_calc);

  // Sold same day
  const sold_same_day_calc = v.sold_same_day !== null ? calc(v.sold_same_day)
    : apply(fa_presented_calc, v.same_day_close_rate);
  const same_day_rate_calc = v.same_day_close_rate !== null ? calc(v.same_day_close_rate)
    : pct(sold_same_day_calc, fa_presented_calc);

  // Sold previous
  const sold_prev = calc(v.sold_previous) || 0;
  const prev_rate_calc = v.prev_close_rate !== null ? calc(v.prev_close_rate)
    : pct(sold_prev, fa_presented_calc);

  // Totals
  const total_sold = (sold_same_day_calc || 0) + sold_prev;
  const total_sold_val = total_sold > 0 ? total_sold : null;
  const normalized_close_rate = pct(total_sold_val, fa_presented_calc);
  const arches_sold = total_sold_val !== null ? Math.round(total_sold_val * (calc(v.arches_per_case) || 1.6)) : null;
  const revenue = arches_sold !== null && calc(v.avg_case_value) !== null
    ? arches_sold * calc(v.avg_case_value) : null;
  const cost_per_lead = cpl_calc;
  const cost_per_show = (calc(v.spend) !== null && shows_calc !== null && shows_calc > 0)
    ? Math.round(calc(v.spend) / shows_calc) : null;
  const cost_per_arch = (calc(v.spend) !== null && arches_sold !== null && arches_sold > 0)
    ? Math.round(calc(v.spend) / arches_sold) : null;
  const roi = (revenue !== null && calc(v.spend) !== null && calc(v.spend) > 0)
    ? Math.round((revenue - calc(v.spend)) / calc(v.spend) * 100) : null;

  // ── Scenarios ───────────────────────────────────────────────────────────
  const loadScenario = (name) => {
    setScenario(name);
    if (name === 'Conservative') {
      setV({ spend: 40000, leads: null, cpl: 280, booked: null, booking_rate: 35,
              shows: null, show_rate: 55, fa_presented: null, fa_rate: 80,
              sold_same_day: null, same_day_close_rate: 13, sold_previous: 3,
              prev_close_rate: null, avg_case_value: 28000, arches_per_case: 1.5 });
    } else if (name === 'Target') {
      setV({ spend: 40000, leads: null, cpl: 180, booked: null, booking_rate: 45,
              shows: null, show_rate: 65, fa_presented: null, fa_rate: 85,
              sold_same_day: null, same_day_close_rate: 22, sold_previous: 5,
              prev_close_rate: null, avg_case_value: 32000, arches_per_case: 1.7 });
    } else if (name === 'Best Case') {
      setV({ spend: 40000, leads: null, cpl: 120, booked: null, booking_rate: 55,
              shows: null, show_rate: 75, fa_presented: null, fa_rate: 90,
              sold_same_day: null, same_day_close_rate: 35, sold_previous: 8,
              prev_close_rate: null, avg_case_value: 38000, arches_per_case: 1.8 });
    } else {
      setV(DEFAULTS);
    }
  };

  const handleRateChange = (rateKey, countKey, prevCount, rateVal) => {
    set(rateKey, rateVal);
    set(countKey, null); // let rate drive count
  };

  const handleCountChange = (countKey, rateKey, val) => {
    set(countKey, val);
    set(rateKey, null); // let count drive rate
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Zap size={22} className="text-cyan-400" /> Flow Calculator
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Click any number to edit. Change a count <em>or</em> a rate — everything cascades automatically.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-400">Load scenario:</span>
          {['Conservative', 'Target', 'Best Case'].map(s => (
            <button key={s} onClick={() => loadScenario(s)}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors font-medium ${
                scenario === s
                  ? 'border-cyan-400 text-cyan-600 bg-cyan-50'
                  : 'border-slate-300 bg-white text-slate-700 hover:border-cyan-300'
              }`}>
              {s}
            </button>
          ))}
          <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => { setV(DEFAULTS); setScenario('Custom'); }}>
            <RefreshCw size={12} /> Reset
          </Button>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-cyan-950/40 border border-cyan-800/30">
        <Info size={14} className="text-cyan-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-cyan-300">
          Enter a <strong>count</strong> (e.g. Leads = 200) to lock that value. Enter a <strong>rate</strong> (e.g. Booking Rate = 40%) to calculate the count. Leave both blank to let the previous stage drive it. Changes cascade downstream automatically.
        </p>
      </div>

      {/* ── FLOW ── */}
      <div className="overflow-x-auto pb-4">
        <div className="flex items-start gap-0 min-w-[1200px]">

          {/* 1. MARKETING SPEND */}
          <StageCard icon={DollarSign} title="Marketing" color="#3B82F6" width="w-44">
            <EditableNum value={v.spend} onChange={val => set('spend', val)} prefix="$" big placeholder="Spend" />
            <SubRow label="Budget" value={v.spend} prefix="$" />
          </StageCard>

          <RateBadge
            rate={cpl_calc !== null ? Math.round(cpl_calc) : null}
            onChange={val => { set('cpl', val); set('leads', null); }}
            label="CPL ($)"
          />

          {/* 2. LEADS */}
          <StageCard icon={Users} title="Leads" color="#8B5CF6" width="w-44">
            <EditableNum value={leads_calc} onChange={val => handleCountChange('leads', 'cpl', val)} big highlight={!v.leads} />
            <div className="mt-2 space-y-0.5">
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">CPL</span>
                <button onClick={() => { set('cpl', null); set('leads', null); }}
                  className="font-semibold tabular-nums text-slate-700 hover:text-cyan-600 cursor-pointer">
                  {fmt$(cpl_calc)}
                </button>
              </div>
            </div>
            <div className="mt-2">
              <EditableNum value={v.cpl} onChange={val => { set('cpl', val); set('leads', null); }}
                prefix="$" suffix="/lead" placeholder="override CPL" muted={!v.cpl} />
            </div>
          </StageCard>

          <RateBadge
            rate={booking_rate_calc}
            onChange={val => handleRateChange('booking_rate', 'booked', leads_calc, val)}
            label="Book Rate"
          />

          {/* 3. BOOKINGS */}
          <StageCard icon={Phone} title="Bookings" color="#06B6D4" width="w-44">
            <EditableNum value={booked_calc} onChange={val => handleCountChange('booked', 'booking_rate', val)} big highlight={!v.booked} />
            <SubRow label="Booking Rate" value={booking_rate_calc} suffix="%" />
            <div className="mt-2">
              <p className="text-xs text-slate-400 mb-1">Override rate:</p>
              <EditableNum value={v.booking_rate}
                onChange={val => handleRateChange('booking_rate', 'booked', leads_calc, val)}
                suffix="%" placeholder="e.g. 40" muted={!v.booking_rate} />
            </div>
          </StageCard>

          <RateBadge
            rate={show_rate_calc}
            onChange={val => handleRateChange('show_rate', 'shows', booked_calc, val)}
            label="Show Rate"
          />

          {/* 4. SHOWS */}
          <StageCard icon={Eye} title="Shows" color="#10B981" width="w-44">
            <EditableNum value={shows_calc} onChange={val => handleCountChange('shows', 'show_rate', val)} big highlight={!v.shows} />
            <SubRow label="Show Rate" value={show_rate_calc} suffix="%" />
            <div className="mt-2">
              <p className="text-xs text-slate-400 mb-1">Override rate:</p>
              <EditableNum value={v.show_rate}
                onChange={val => handleRateChange('show_rate', 'shows', booked_calc, val)}
                suffix="%" placeholder="e.g. 65" muted={!v.show_rate} />
            </div>
          </StageCard>

          <RateBadge
            rate={fa_rate_calc}
            onChange={val => handleRateChange('fa_rate', 'fa_presented', shows_calc, val)}
            label="FA Rate"
          />

          {/* 5. FA PRESENTED */}
          <StageCard icon={Target} title="FA Presented" color="#F59E0B" width="w-44" highlight>
            <EditableNum value={fa_presented_calc} onChange={val => handleCountChange('fa_presented', 'fa_rate', val)} big highlight={!v.fa_presented} />
            <SubRow label="% of Shows" value={fa_rate_calc} suffix="%" />
            <div className="mt-2">
              <p className="text-xs text-slate-400 mb-1">Override rate:</p>
              <EditableNum value={v.fa_rate}
                onChange={val => handleRateChange('fa_rate', 'fa_presented', shows_calc, val)}
                suffix="%" placeholder="e.g. 85" muted={!v.fa_rate} />
            </div>
          </StageCard>

          <div className="flex flex-col gap-2 px-2 self-start mt-12">
            <ArrowRight size={14} className="text-slate-300" />
          </div>

          {/* 6. SOLD */}
          <StageCard icon={TrendingUp} title="FA Sold" color="#EF4444" width="w-52" highlight>
            {/* Same Day */}
            <div className="mb-3">
              <p className="text-xs text-slate-500 font-medium mb-1">Same Day</p>
              <EditableNum value={sold_same_day_calc} onChange={val => handleCountChange('sold_same_day', 'same_day_close_rate', val)} big highlight />
              <SubRow label="Same Day Close %" value={same_day_rate_calc} suffix="%" />
              <div className="mt-1">
                <EditableNum value={v.same_day_close_rate}
                  onChange={val => handleRateChange('same_day_close_rate', 'sold_same_day', fa_presented_calc, val)}
                  suffix="%" placeholder="e.g. 22" muted={!v.same_day_close_rate} />
              </div>
            </div>
            {/* Previous */}
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs text-slate-500 font-medium mb-1">From Previous</p>
              <EditableNum value={v.sold_previous} onChange={val => set('sold_previous', val)} />
              <SubRow label="Prev Close %" value={prev_rate_calc} suffix="%" />
            </div>
            {/* Total */}
            <div className="border-t border-slate-200 pt-2 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">TOTAL SOLD</span>
                <span className="text-lg font-bold text-slate-900 tabular-nums">{total_sold_val ?? '—'}</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-slate-400">Normalized Close %</span>
                <span className={`text-xs font-semibold ${normalized_close_rate !== null ? (normalized_close_rate >= 30 ? 'text-green-600' : normalized_close_rate >= 15 ? 'text-amber-600' : 'text-red-500') : 'text-slate-400'}`}>
                  {normalized_close_rate !== null ? normalized_close_rate.toFixed(1) + '%' : '—'}
                </span>
              </div>
            </div>
          </StageCard>

          <div className="flex flex-col gap-2 px-2 self-start mt-12">
            <ArrowRight size={14} className="text-slate-300" />
          </div>

          {/* 7. REVENUE */}
          <StageCard icon={DollarSign} title="Revenue" color="#16A34A" width="w-52">
            <p className="text-xs font-semibold text-slate-900 text-center tabular-nums text-2xl">
              {revenue !== null ? '$' + Math.round(revenue).toLocaleString() : '—'}
            </p>
            <div className="mt-3 space-y-2">
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Arches per Case (avg)</p>
                <EditableNum value={v.arches_per_case} onChange={val => set('arches_per_case', val)} suffix="x" muted={!v.arches_per_case} />
              </div>
              <SubRow label="Arches Sold" value={arches_sold} />
              <div className="border-t border-slate-100 pt-2">
                <p className="text-xs text-slate-400 mb-0.5">Avg Case Value ($)</p>
                <EditableNum value={v.avg_case_value} onChange={val => set('avg_case_value', val)} prefix="$" />
              </div>
            </div>
          </StageCard>

        </div>
      </div>

      {/* ── SUMMARY KPI CARDS ── */}
      {(calc(v.spend) !== null) && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-4 rounded-full bg-cyan-400" />
            <h2 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Summary KPIs</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: 'Total Spend', value: fmt$(calc(v.spend)), color: '#3B82F6' },
              { label: 'Cost per Lead', value: fmt$(cost_per_lead), color: '#8B5CF6' },
              { label: 'Cost per Show', value: fmt$(cost_per_show), color: '#10B981' },
              { label: 'Cost per Arch', value: fmt$(cost_per_arch), color: '#F59E0B' },
              { label: 'Total Revenue', value: fmt$(revenue), color: '#16A34A' },
              { label: 'ROI', value: roi !== null ? roi + '%' : '—', color: roi !== null && roi >= 200 ? '#16A34A' : roi !== null && roi >= 0 ? '#F59E0B' : '#EF4444' },
            ].map(({ label, value, color }) => (
              <Card key={label} className="bg-white shadow-sm" style={{ borderTop: `3px solid ${color}` }}>
                <CardContent className="p-4">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{label}</p>
                  <p className="text-xl font-semibold tabular-nums font-heading text-slate-900">{value}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ── FUNNEL VISUALIZATION ── */}
      {leads_calc !== null && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-4 rounded-full bg-purple-400" />
            <h2 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Funnel Shape</h2>
          </div>
          <Card className="bg-white shadow-sm">
            <CardContent className="p-6">
              {[
                { label: 'Leads', value: leads_calc, max: leads_calc, color: '#8B5CF6' },
                { label: 'Booked', value: booked_calc, max: leads_calc, color: '#06B6D4' },
                { label: 'Shows', value: shows_calc, max: leads_calc, color: '#10B981' },
                { label: 'FA Presented', value: fa_presented_calc, max: leads_calc, color: '#F59E0B' },
                { label: 'FA Sold', value: total_sold_val, max: leads_calc, color: '#EF4444' },
              ].filter(s => s.value !== null).map((stage, i) => {
                const pctWidth = leads_calc > 0 ? Math.round(stage.value / leads_calc * 100) : 0;
                return (
                  <div key={stage.label} className="mb-3 last:mb-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-600">{stage.label}</span>
                      <span className="text-xs tabular-nums font-semibold text-slate-800">
                        {stage.value.toLocaleString()} <span className="text-slate-400 font-normal">({pctWidth}%)</span>
                      </span>
                    </div>
                    <div className="h-6 bg-slate-100 rounded-md overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pctWidth}%` }}
                        transition={{ duration: 0.5, delay: i * 0.08 }}
                        className="h-full rounded-md flex items-center pl-2"
                        style={{ background: stage.color, minWidth: stage.value > 0 ? '2px' : 0 }}
                      >
                        {pctWidth > 10 && (
                          <span className="text-white text-xs font-semibold">{stage.value.toLocaleString()}</span>
                        )}
                      </motion.div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
